/**
 * What a form currently holds, as a string, for telling "changed" from
 * "merely opened".
 *
 * Read off the controls rather than from whatever the form would SEND. The
 * obvious thing is to serialise the request body and compare two of them,
 * and it does not work: a body is built for sending, so it mints ids for
 * the things it is about to create — a wallet move, a new leg, a new tag —
 * and two bodies built a second apart from an untouched form differ in
 * every one of them. A form that is always dirty asks "save your changes?"
 * of somebody who typed nothing, which is how a prompt teaches people to
 * dismiss prompts.
 *
 * The controls cannot drift that way: their values are what the user put
 * there, and nothing else.
 *
 * Order matters and is meant to. Adding or removing a split row changes the
 * sequence, which IS a change — and comparing by position needs no ids,
 * which is the whole point here.
 */
export function formSnapshot(container: HTMLElement): string {
  const parts: string[] = [];
  const controls = container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input, select, textarea',
  );
  for (const control of controls) {
    // A file input's value is a path the page cannot set and the user cannot
    // meaningfully edit; attachments have their own save path entirely.
    if (control instanceof HTMLInputElement && control.type === 'file') continue;
    const value = control instanceof HTMLInputElement
      && (control.type === 'checkbox' || control.type === 'radio')
      ? String(control.checked)
      : control.value;
    // The id when there is one, so a reordering of equal-valued controls is
    // not mistaken for an edit; the index otherwise.
    parts.push(`${control.id || String(parts.length)}=${value}`);
  }
  return parts.join('\u0001');
}
